import { Action } from 'redux-actions';
import { call, fork, put, select, takeEvery } from 'redux-saga/effects';
import api from 'services/api';
import {
  createPost,
  createPostSuccess,
  deletePost,
  deletePostSuccess,
  fetchPosts,
  fetchPostsFailed,
  fetchPostsSuccess,
  modifyPost,
  modifyPostSuccess,
} from 'store/DirectPosts/slice';
import { getSelectedCoinInfo } from 'store/TokenTeam/selectors';
import { selectCoin } from 'store/TokenTeam/slice';
import { DirectPost, EditablePost } from './types';
import * as Sentry from '@sentry/nextjs';

function* fetchDirectPostsFlow() {
  try {
    const selectedCoin = yield select(getSelectedCoinInfo);

    const { data: response } = yield call(
      api.get,
      `/direct-post/list/${selectedCoin.id}`,
    );

    if (!response.meta.success) {
      yield put(fetchPostsFailed());
    } else {
      yield put(fetchPostsSuccess(response.data));
    }
  } catch (error) {
    // continue
    yield put(fetchPostsFailed());
  }
}

function* fetchDirectPostsFlowWatcher() {
  yield takeEvery(
    [
      fetchPosts.toString(),
      createPostSuccess.toString(),
      modifyPostSuccess.toString(),
      deletePostSuccess.toString(),
      selectCoin.toString(),
    ],
    fetchDirectPostsFlow,
  );
}

function* newPostFlow({ payload }: Action<{ newPost: EditablePost }>) {
  try {
    if (!payload) {
      return;
    }

    const { newPost } = payload;

    const { data: response } = yield call(api.post, `/direct-post/create`, {
      coinId: newPost.coinId,
      title: newPost.title,
      text: newPost.text,
      headerImage: newPost.headerImage,
      link: newPost.link,
    });

    if (response.meta.success) {
      yield put(createPostSuccess());
    }
  } catch (error) {
    Sentry.captureException(error);
  }
}

function* newPostFlowWatcher() {
  yield takeEvery(createPost.toString(), newPostFlow);
}

function* updatePostFlow({ payload }: Action<{ updatedPost: DirectPost }>) {
  try {
    if (!payload) {
      return;
    }

    const { updatedPost } = payload;

    const { data: response } = yield call(api.post, `/direct-post/update`, {
      directPost: {
        id: updatedPost.id,
        title: updatedPost.title,
        text: updatedPost.text,
        headerImage: updatedPost.headerImage,
        link: updatedPost.link,
      },
    });

    if (response.meta.success) {
      yield put(modifyPostSuccess());
    }
  } catch (error) {
    Sentry.captureException(error);
  }
}

function* updatePostFlowWatcher() {
  yield takeEvery(modifyPost.toString(), updatePostFlow);
}

function* deletePostFlow({ payload }: Action<{ deletedPost: DirectPost }>) {
  try {
    if (!payload) {
      return;
    }

    const { deletedPost } = payload;

    const { data: response } = yield call(api.post, `/direct-post/delete`, {
      directPostId: deletedPost.id,
    });

    if (response.meta.success) {
      yield put(deletePostSuccess());
    }
  } catch (error) {
    Sentry.captureException(error);
  }
}

function* deletePostFlowWatcher() {
  yield takeEvery(deletePost.toString(), deletePostFlow);
}

export default [
  fork(fetchDirectPostsFlowWatcher),
  fork(newPostFlowWatcher),
  fork(updatePostFlowWatcher),
  fork(deletePostFlowWatcher),
];
